
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Container, Grid, Card, CardContent, Typography, Chip, Stack,
  Button, IconButton, Paper, Modal, Fade, Backdrop, CircularProgress,
  Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions,
  useMediaQuery, useTheme,
} from "@mui/material";
import { createTheme, ThemeProvider, alpha } from "@mui/material/styles";
import {
  ArrowBack      as ArrowBackIcon,
  Refresh        as RefreshIcon,
  Close          as CloseIcon,
  CalendarMonth  as CalendarIcon,
  AccessTime     as TimeIcon,
  Person         as PersonIcon,
  CancelOutlined as CancelIcon,
  WarningAmberRounded as WarnIcon,
  LocationOn     as LocationIcon,
  LocalHospital  as NursingIcon,
  FitnessCenter  as PhysioIcon,
  MedicalServices as MedicalIcon,
  Phone          as PhoneIcon,
  History        as HistoryIcon,
  CurrencyRupee  as CurrencyRupeeIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import axios from 'axios'
// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    primary:    { main: "#006D77", light: "#4A9FA8", dark: "#003D45" },
    success:    { main: "#16A34A" },
    warning:    { main: "#D97706" },
    error:      { main: "#DC2626" },
    background: { default: "#F9FAFB", paper: "#FFFFFF" },
  },
  typography: {
    fontFamily: "'DM Sans', sans-serif",
    h5: { fontFamily: "'Sora', sans-serif", fontWeight: 900 },
    h6: { fontFamily: "'Sora', sans-serif", fontWeight: 800 },
    button: { fontFamily: "'DM Sans', sans-serif", fontWeight: 700, textTransform: "none" },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: { styleOverrides: { root: { textTransform: "none", fontWeight: 700 } } },
  },
});

// ─── API / Auth ───────────────────────────────────────────────────────────────
const API = process.env.REACT_APP_API_BASE_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const getUserId = () =>
  sessionStorage.getItem("userId") || localStorage.getItem("userId") || null;

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CFG = {
  pending:   { bg: "#FEF3C7", text: "#D97706", label: "Pending Review" },
  confirmed: { bg: "#DBEAFE", text: "#2563EB", label: "Confirmed"      },
  completed: { bg: "#D1FAE5", text: "#059669", label: "Completed"      },
  cancelled: { bg: "#FEE2E2", text: "#DC2626", label: "Cancelled"      },
};

// ─── Category meta ────────────────────────────────────────────────────────────
const CAT_META = {
  nursing: {
    icon:     <NursingIcon />,
    gradient: "linear-gradient(135deg, #006D77, #004E56)",
    accent:   "#006D77",
    lightBg:  "#E0F7FA",
    emoji:    "🏥",
  },
  physiotherapy: {
    icon:     <PhysioIcon />,
    gradient: "linear-gradient(135deg, #b8654a, #8B3E2F)",
    accent:   "#b8654a",
    lightBg:  "#FBE9E7",
    emoji:    "🦾",
  },
};

// ─── Small reusable chips ─────────────────────────────────────────────────────
const StatusChip = ({ value }) => {
  const c = STATUS_CFG[value] || { bg: "#F1F5F9", text: "#64748B", label: value };
  return (
    <Chip label={c.label} size="small"
      sx={{ bgcolor: c.bg, color: c.text, fontWeight: 700, fontSize: 11, borderRadius: "16px", px: 0.5, pointerEvents: "none" }} />
  );
};

// ─── InfoItem ─────────────────────────────────────────────────────────────────
const InfoItem = ({ label, value }) => (
  <Box sx={{ background: "#F8FAFC", borderRadius: 2, p: "9px 12px", border: "1px solid #E5E7EB" }}>
    <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4, mb: 0.3 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#111827", wordBreak: "break-word" }}>
      {value || "—"}
    </Typography>
  </Box>
);

// ─── Cancel confirmation dialog ───────────────────────────────────────────────
const CancelDialog = ({ booking, onConfirm, onClose, loading }) => (
  <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
    <DialogContent sx={{ textAlign: "center", pt: 4, pb: 2 }}>
      <Box sx={{
        width: 60, height: 60, borderRadius: "50%", bgcolor: "#FEF3C7",
        border: "2px solid #FDE68A", display: "flex", alignItems: "center",
        justifyContent: "center", mx: "auto", mb: 2,
      }}>
        <WarnIcon sx={{ fontSize: 30, color: "#D97706" }} />
      </Box>
      <Typography sx={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800, mb: 1, color: "#111827" }}>
        Cancel Booking?
      </Typography>
      <Typography sx={{ fontSize: 14, color: "#6B7280", mb: 2, lineHeight: 1.7 }}>
        Are you sure you want to cancel this booking?
      </Typography>
      <Box sx={{ bgcolor: "#E5E7EB", borderRadius: 2, py: 1, px: 2, fontFamily: "monospace", fontSize: 14, fontWeight: 800, color: "#006D77" }}>
        {booking.booking_ref}
      </Box>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
      <Button fullWidth variant="outlined" onClick={onClose}
        sx={{ borderColor: "#E5E7EB", color: "#6B7280", py: 1.25, borderRadius: 2 }}>
        Keep Booking
      </Button>
      <Button fullWidth variant="contained" onClick={() => onConfirm(booking._id)}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <CancelIcon />}
        sx={{ bgcolor: "#DC2626", py: 1.25, borderRadius: 2, fontWeight: 800, "&:hover": { bgcolor: "#B91C1C" } }}>
        {loading ? "Cancelling..." : "Yes, Cancel"}
      </Button>
    </DialogActions>
  </Dialog>
);

// ─── Booking Detail Modal ─────────────────────────────────────────────────────
const BookingDetailModal = ({ booking, onClose, onCancelRequest }) => {
  const muiTheme   = useTheme();
  const fullScreen = useMediaQuery(muiTheme.breakpoints.down("sm"));
  if (!booking) return null;

  const meta      = CAT_META[booking.category] || CAT_META.nursing;
  const canCancel = ["pending", "confirmed"].includes(booking.status);
  const handleInvoiceDownload=async(order_id)=>{
    try {
          const token = sessionStorage.getItem("token");

          const response = await axios.get(
            `${process.env.REACT_APP_API_INVOICE}/nursing-invoice/download/${order_id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const download_url=response.data.download_url
          window.open(download_url, "_blank");
        } catch (error) {
          console.error("Invoice download failed:", error);
        }
}



  return (
    <Modal open onClose={onClose} closeAfterTransition BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 300, sx: { backdropFilter: "blur(5px)", backgroundColor: "rgba(0,0,0,0.5)" } }}>
      <Fade in>
        <Box sx={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%,-50%)",
          width: fullScreen ? "95vw" : 520,
          maxHeight: "90vh", overflowY: "auto",
          bgcolor: "#fff", borderRadius: 3,
          boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
        }}>
          {/* Gradient header */}
          <Box sx={{ background: meta.gradient, p: "24px 22px 18px", position: "relative", overflow: "hidden" }}>
            <Box sx={{ position: "absolute", top: -40, right: -40, width: 150, height: 150, borderRadius: "50%", background: "rgba(255,255,255,0.08)" }} />
            <Box sx={{ position: "absolute", bottom: -30, left: -20, width: 110, height: 110, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
            <IconButton onClick={onClose} size="small" sx={{
              position: "absolute", top: 12, right: 12, zIndex: 2,
              bgcolor: "rgba(255,255,255,0.2)", color: "#fff",
              "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
            }}>
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography sx={{ fontSize: 32, lineHeight: 1, mb: 0.75 }}>{meta.emoji}</Typography>
              <Typography sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, color: "#fff", fontSize: { xs: 15, sm: 17 }, mb: 0.5 }}>
                {booking.service_name}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                <Box sx={{ display: "inline-block", bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 12, fontWeight: 800, px: 1.5, py: 0.3, borderRadius: 1.5, fontFamily: "monospace" }}>
                  {booking.booking_ref}
                </Box>
                <Box sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", fontSize: 11, fontWeight: 700, px: 1.25, py: 0.3, borderRadius: 10, textTransform: "capitalize" }}>
                  {booking.category}
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                  <CalendarIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.75)" }} />
                  <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.9)", fontWeight: 600 }}>{booking.scheduled_date}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                  <TimeIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.75)" }} />
                  <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.9)", fontWeight: 600 }}>{booking.scheduled_time}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Body */}
          <Box sx={{ p: { xs: "14px 14px 22px", sm: "18px 20px 26px" } }}>
            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
              <StatusChip value={booking.status} />
            </Box>

            <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#006D77", textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>
              Patient Details
            </Typography>
            <Grid container spacing={1} mb={2}>
              <Grid item xs={6}><InfoItem label="Name"  value={booking.patient_name}  /></Grid>
              <Grid item xs={6}><InfoItem label="Phone" value={booking.patient_phone} /></Grid>
              <Grid item xs={12}><InfoItem label="Address" value={booking.patient_address} /></Grid>
            </Grid>

            <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#006D77", textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>
              Appointment
            </Typography>
            <Grid container spacing={1} mb={2}>
              <Grid item xs={6}><InfoItem label="Date"      value={booking.scheduled_date} /></Grid>
              <Grid item xs={6}><InfoItem label="Time Slot" value={booking.scheduled_time} /></Grid>
            </Grid>

            {booking.notes && (
              <Box sx={{ bgcolor: "#F8FAFC", border: "1px solid #E5E7EB", borderRadius: 2, p: "10px 12px", mb: 2 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4, mb: 0.5 }}>Notes</Typography>
                <Typography sx={{ fontSize: 13, color: "#374151", lineHeight: 1.7 }}>{booking.notes}</Typography>
              </Box>
            )}
                    {booking.invoice &&(<Button
                        variant="contained"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => {
                          handleInvoiceDownload(booking.booking_ref)
                        }}
                        sx={{ 
                          bgcolor: '#006D77', 
                          '&:hover': { bgcolor: '#005560' },
                          textTransform: 'none',
                          marginLeft:"8px"
                        }}
                      >
                        Download Invoice
                      </Button>)}

            {booking.created_at && (
              <Typography sx={{ fontSize: 11, color: "#9CA3AF", textAlign: "center", mb: canCancel ? 1.5 : 0 }}>
                Booked on {new Date(booking.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
              </Typography>
            )}

            {canCancel && (
              <Button fullWidth variant="outlined" startIcon={<CancelIcon />}
                onClick={() => onCancelRequest(booking)}
                sx={{ mt: 0.5, py: 1.25, borderRadius: 2, fontWeight: 800, fontSize: 14, borderColor: "#FECACA", color: "#DC2626", "&:hover": { bgcolor: "#FEE2E2", borderColor: "#EF4444" } }}>
                Cancel This Booking
              </Button>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

// ─── Single booking card ──────────────────────────────────────────────────────
const BookingCard = ({ booking, onView, onCancelRequest,onPay }) => {
  const meta      = CAT_META[booking.category] || CAT_META.nursing;
  const canCancel = ["pending", "confirmed"].includes(booking.status);

  return (
    <Card elevation={2} sx={{
      borderRadius: 3, mb: 2, overflow: "hidden",
      border: "1px solid #E5E7EB",
      "&:hover": { boxShadow: "0 3px 12px rgba(0,0,0,0.1)" },
    }}>
      <Box sx={{ height: 4, background: meta.gradient }} />
      <CardContent sx={{ p: 2.5 }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flex: 1, mr: 1 }}>
            <Box sx={{
              width: 42, height: 42, borderRadius: 2, flexShrink: 0,
              background: meta.gradient, display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", boxShadow: `0 3px 10px ${meta.accent}44`,
            }}>
              {React.cloneElement(meta.icon, { sx: { fontSize: 22 } })}
            </Box>
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#111827", lineHeight: 1.3 }}>
                {booking.service_name}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
                <code style={{ fontSize: 11, color: meta.accent, background: "#E5E7EB", padding: "1px 7px", borderRadius: 5, fontWeight: 700 }}>
                  {booking.booking_ref}
                </code>
                <Chip label={booking.category} size="small"
                  sx={{ bgcolor: meta.lightBg, color: meta.accent, fontWeight: 700, fontSize: 10, textTransform: "capitalize", height: 18 }} />
              </Box>
            </Box>
          </Box>
          <StatusChip value={booking.status} />
        </Box>

        {/* Appointment row */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1 }}>
          {[
            { icon: <CalendarIcon sx={{ fontSize: 13, color: meta.accent }} />, text: booking.scheduled_date },
            { icon: <TimeIcon     sx={{ fontSize: 13, color: meta.accent }} />, text: booking.scheduled_time },
            { icon: <PersonIcon   sx={{ fontSize: 13, color: meta.accent }} />, text: booking.patient_name   },
            { icon: <PhoneIcon    sx={{ fontSize: 13, color: meta.accent }} />, text: booking.patient_phone  },
          ].filter(x => x.text).map((item, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {item.icon}
              <Typography sx={{ fontSize: 12, color: "#374151", fontWeight: 500 }}>{item.text}</Typography>
            </Box>
          ))}
        </Box>

        {/* Address */}
        {booking.patient_address && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mb: 1 }}>
            <LocationIcon sx={{ fontSize: 13, color: meta.accent, mt: 0.15, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12, color: "#6B7280", fontWeight: 500, lineHeight: 1.4 }}>
              {booking.patient_address}
            </Typography>
          </Box>
        )}

        {/* Actions */}
        <Box sx={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          borderTop: "1px solid #E5E7EB", pt: 1.5, mt: 0.5, flexWrap: "wrap", gap: 1,
        }}>
          {booking.created_at && (
            <Typography sx={{ fontSize: 11, color: "#9CA3AF" }}>
              {new Date(booking.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: 1, ml: "auto", flexWrap: "wrap" }}>
              {canCancel && (
                <Button variant="outlined" size="small"
                  onClick={(e) => { e.stopPropagation(); onCancelRequest(booking); }}
                  sx={{ borderColor: "#FECACA", color: "#DC2626", borderRadius: 3, fontWeight: 700, fontSize: 12, px: 1.5,
                    "&:hover": { bgcolor: "#FEE2E2", borderColor: "#EF4444" } }}>
                  Cancel
                </Button>
              )}

              {/* ── PAY button — shown when completed + amount set + not paid ── */}
              {booking.status === "completed" && booking.amount_due && booking.payment_status !== "paid" && (
                <Button variant="contained" size="small"
                  onClick={(e) => { e.stopPropagation(); onPay(booking); }}
                  startIcon={<CurrencyRupeeIcon sx={{ fontSize: 13 }} />}
                  sx={{ bgcolor: "#16a34a", borderRadius: 3, fontWeight: 800, fontSize: 12, px: 1.5,
                    "&:hover": { bgcolor: "#166534" }, animation: "pulse 2s infinite",
                    "@keyframes pulse": {
                      "0%,100%": { boxShadow: "0 0 0 0 rgba(22,163,74,0.4)" },
                      "50%":     { boxShadow: "0 0 0 6px rgba(22,163,74,0)" },
                    },
                  }}>
                  Pay ₹{booking.amount_due}
                </Button>
              )}

              {/* ── Paid badge ── */}
              {booking.payment_status === "paid" && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5,
                  bgcolor: "#D1FAE5", px: 1.25, py: 0.5, borderRadius: 3 }}>
                  <Typography fontSize={12} fontWeight={800} color="#059669">✅ Paid ₹{booking.amount_paid}</Typography>
                </Box>
              )}

              <Button variant="outlined" size="small" onClick={() => onView(booking)}
                sx={{ borderColor: meta.accent, bgcolor: meta.accent, color: "#fff", borderRadius: 3,
                  fontWeight: 700, fontSize: 12, px: 2, "&:hover": { opacity: 0.88 } }}>
                View Details
              </Button>
            </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Shared bookings body (used in both page & dialog) ────────────────────────
  const BookingsBody = ({ bookings, loading, error, filterStatus, setFilterStatus, onView, onCancelRequest, onRetry, isDialog, onPay }) => {
  const filters = [
    { id: "all",       label: "All"           },
    { id: "pending",   label: "Pending Review" },
    { id: "confirmed", label: "Confirmed"      },
    { id: "completed", label: "Completed"      },
    { id: "cancelled", label: "Cancelled"      },
  ];

  const filtered = filterStatus === "all" ? bookings : bookings.filter(b => b.status === filterStatus);

  const nursingCount   = bookings.filter(b => b.category === "nursing").length;
  const physioCount    = bookings.filter(b => b.category === "physiotherapy").length;
  const completedCount = bookings.filter(b => b.status === "completed").length;
  const pendingCount   = bookings.filter(b => b.status === "pending").length;

  return (
    <>
      {/* Filter chips */}
      <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 1, "&::-webkit-scrollbar": { display: "none" }, mb: 2 }}>
        {filters.map((filter) => (
          <Chip key={filter.id}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <span>{filter.label}</span>
                {filter.id !== "all" && (
                  <Box sx={{
                    bgcolor: filterStatus === filter.id ? "rgba(255,255,255,0.3)" : "#006D77",
                    color: "#fff", fontSize: 10, fontWeight: 700,
                    px: 0.6, py: 0.4, borderRadius: 1, minWidth: 18, textAlign: "center",
                  }}>
                    {bookings.filter(b => b.status === filter.id).length}
                  </Box>
                )}
              </Box>
            }
            onClick={() => setFilterStatus(filter.id)}
            sx={{
              bgcolor: filterStatus === filter.id ? "#006D77" : "white",
              color:   filterStatus === filter.id ? "#fff"    : "black",
              fontWeight: 600, fontSize: 12,
              border: filterStatus === filter.id ? "1px solid transparent" : "1px solid #006D77",
              cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
              "&:hover": { bgcolor: filterStatus === filter.id ? "#005960" : "#E5E7EB" },
            }}
          />
        ))}
      </Box>

      {/* Stats bar — only when there are bookings */}
      {!loading && bookings.length > 0 && (
        <Box sx={{ display: "flex", gap: 1, mb: 2.5, flexWrap: "wrap" }}>
          {[
            { label: "Total",         val: bookings.length },
            { label: "Nursing",       val: nursingCount    },
            { label: "Physiotherapy", val: physioCount     },
            { label: "Completed",     val: completedCount  },
            { label: "Pending",       val: pendingCount    },
          ].map((s) => (
            <Paper key={s.label} elevation={1} sx={{
              flex: 1, minWidth: isDialog ? 60 : 90,
              bgcolor: "#006D77", p: isDialog ? 1.25 : 2,
              borderRadius: 2.5, textAlign: "center",
            }}>
              <Typography sx={{ fontSize: isDialog ? 18 : 22, fontWeight: 700, color: "#fff", mb: 0.25, fontFamily: "'Sora',sans-serif" }}>
                {s.val}
              </Typography>
              <Typography sx={{ fontSize: isDialog ? 10 : 11, color: "rgba(255,255,255,0.85)", lineHeight: 1.2 }}>{s.label}</Typography>
            </Paper>
          ))}
        </Box>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <CircularProgress size={40} sx={{ color: "#006D77" }} />
          <Typography mt={1.5} color="text.secondary" fontSize={13}>Fetching your bookings…</Typography>
        </Box>
      )}

      {/* Error */}
      {!loading && error && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Alert severity="error" sx={{ borderRadius: 2, mb: 2, maxWidth: 420, mx: "auto" }}>{error}</Alert>
          <Button variant="contained" onClick={onRetry}
            sx={{ bgcolor: "#006D77", borderRadius: 2, fontWeight: 700, "&:hover": { bgcolor: "#005960" } }}>
            Try Again
          </Button>
        </Box>
      )}

      {/* Empty */}
      {!loading && !error && filtered.length === 0 && (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography sx={{ fontSize: 56, mb: 1.5 }}>🏥</Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#111827", mb: 1, fontFamily: "'Sora',sans-serif" }}>
            No Bookings Found
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
            {filterStatus === "all"
              ? "You haven't booked any nursing or physiotherapy services yet."
              : `No "${filters.find(f => f.id === filterStatus)?.label}" bookings found.`}
          </Typography>
        </Box>
      )}

      {/* Cards */}

      {!loading && !error && filtered.map((b) => (
        <BookingCard key={b._id} booking={b} onView={onView} onCancelRequest={onCancelRequest} onPay={onPay} />
      ))}
    </>
  );
};

export default function MyNurPhyBookingsPage({ open, onClose }) {
  const muiTheme   = useTheme();
  const isMobile   = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const navigate   = useNavigate();

  const isDialogMode = open !== undefined && onClose !== undefined;

  const [bookings,     setBookings]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [refreshing,   setRefreshing]   = useState(false);
  const [error,        setError]        = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedBook, setSelectedBook] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling,   setCancelling]   = useState(false);
  const [snack,        setSnack]        = useState({ open: false, msg: "", sev: "success" });

  const notify = (msg, sev = "success") => setSnack({ open: true, msg, sev });
// Load Razorpay script once
useEffect(() => {
  if (document.getElementById("razorpay-script")) return;
  const script    = document.createElement("script");
  script.id       = "razorpay-script";
  script.src      = "https://checkout.razorpay.com/v1/checkout.js";
  script.async    = true;
  document.body.appendChild(script);
}, []);
  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchBookings = useCallback(async () => {
    const userId = getUserId();
    if (!userId) { setError("Please log in to view your bookings."); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(
        `${API}/nursingphysiotherapy/bookings?user_id=${encodeURIComponent(userId)}`,
        { headers: getAuthHeader() }
      );
      const data = await res.json();
      // if (data.success) setBookings(data.bookings || []);
      if (data.success) {
        const sorted = (data.bookings || []).sort((a, b) => {
          const order = (bk) => {
            // Payment due (completed + amount set + unpaid) → top priority
            if (bk.status === "completed" && bk.amount_due && bk.payment_status !== "paid") return 0;
            if (bk.status === "pending")   return 1;
            if (bk.status === "confirmed") return 2;
            if (bk.status === "completed") return 3;  // completed + paid or no amount
            return 4;  // cancelled
          };
          return order(a) - order(b);
        });
        setBookings(sorted);
      }
      else setError(data.error || "Failed to load bookings");
    } catch {
      setError("Unable to load bookings. Please try again.");
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  // Page mode: fetch on mount
  useEffect(() => {
    if (!isDialogMode) fetchBookings();
  }, [isDialogMode, fetchBookings]);

  // Dialog mode: fetch every time dialog opens, reset state
  useEffect(() => {
    if (isDialogMode && open) {
      setBookings([]);
      setFilterStatus("all");
      setError("");
      fetchBookings();
    }
  }, [isDialogMode, open, fetchBookings]);

  const onRefresh = () => { setRefreshing(true); fetchBookings(); };
// ── Pay ────────────────────────────────────────────────────────────────────
const handlePay = async (booking) => {
  try {
    // Step 1: Create Razorpay order
    const res  = await fetch(
      `${API}/nursingphysiotherapy/bookings/${booking.booking_ref}/create-payment`,
      { method: "POST", headers: { "Content-Type": "application/json", ...getAuthHeader() } }
    );
    const data = await res.json();
    if (!data.success) { notify(data.error || "Could not initiate payment", "error"); return; }

    // Step 2: Open Razorpay checkout
    const options = {
      key:         process.env.REACT_APP_RAZORPAY_KEY,
      amount:      data.amount,
      currency:    data.currency,
      order_id:    data.order_id,
      name:        "Ayuja Health",
      description: booking.service_name,
      prefill:     { name: booking.patient_name, contact: booking.patient_phone },
      theme:       { color: "#006D77" },
      handler: async (response) => {
        // Step 3: Verify on backend
        const vRes  = await fetch(
          `${API}/nursingphysiotherapy/bookings/${booking.booking_ref}/verify-payment`,
          {
            method:  "POST",
            headers: { "Content-Type": "application/json", ...getAuthHeader() },
            body: JSON.stringify({
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
            }),
          }
        );
        const vData = await vRes.json();
        if (vData.success) {
          notify("Payment successful! ✓");
          const posOrderData={ 
          service_type:"nursing",
            order_id: booking.booking_ref,
            items: booking.service_name,
            patient_details: booking,
            subtotal: parseInt(data.amount/100) ||0 ,
            discount: 0,
            total_amount: parseInt(data.amount/100)  ||0,
            payment: { method: "Online" },
            created_at: new Date().toISOString()
          }
            try {
              const invoice_response = await axios.post(
                `${process.env.REACT_APP_API_INVOICE}/invoice/pos_invoice`,
                posOrderData,
              );
        
            const invoice = invoice_response.data.invoice_details;
            if (invoice.storage_type === "s3") {
              // Open CloudFront URL
              window.open(invoice.cloudfront_url, "_blank");
            } else if (invoice.storage_type === "local") {
              try {
              const token = sessionStorage.getItem("token");
  
              const response = await axios.get(
                `${process.env.REACT_APP_API_INVOICE}/nursing-invoice/download/${posOrderData.order_id}`,
                {
                  responseType: "blob",
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              );
  
              const blob = new Blob([response.data], { type: "application/pdf" });
              const url = window.URL.createObjectURL(blob);
  
              window.open(url, "_blank");
  
            } catch (error) {
              console.error("Invoice download failed:", error);
            }
            }
            } catch (error) {
              console.error("Invoice generation failed:", error);
            }
          fetchBookings();
        } else {
          notify("Payment verification failed. Contact support.", "error");
        }
      },
      modal: {
        ondismiss: () => notify("Payment cancelled", "warning"),
      },
    };

    if (!window.Razorpay) {
      notify("Payment gateway not loaded. Please refresh.", "error");
      return;
    }
    const rzp = new window.Razorpay(options);
    rzp.open();
  } catch {
    notify("Payment failed. Please try again.", "error");
  }
};
  // ── Cancel ─────────────────────────────────────────────────────────────────
  const handleCancel = async (bookingId) => {
    setCancelling(true);
    try {
      const res = await fetch(
        `${API}/nursingphysiotherapy/bookings/${bookingId}/cancel`,
        { method: "PATCH", headers: { "Content-Type": "application/json", ...getAuthHeader() } }
      );
      const data = await res.json();
      if (data.success) {
        setCancelTarget(null);
        setSelectedBook(null);
        notify("Booking cancelled successfully");
        await fetchBookings();
      } else {
        notify(data.error || "Cancellation failed", "error");
      }
    } catch {
      notify("Cancellation failed. Please try again.", "error");
    }
    setCancelling(false);
  };

  // Shared props for BookingsBody
  const bodyProps = {
    bookings, loading, error, filterStatus, setFilterStatus,
    onView:          (b) => setSelectedBook(b),
    onCancelRequest: (b) => { setSelectedBook(null); setCancelTarget(b); },
    onRetry:         fetchBookings,
    isDialog:        isDialogMode,
    onPay:           handlePay, 
  };

  // ── Modals (shared in both modes) ──────────────────────────────────────────
  const Modals = (
    <>
      {selectedBook && (
        <BookingDetailModal
          booking={selectedBook}
          onClose={() => setSelectedBook(null)}
          onCancelRequest={(b) => { setSelectedBook(null); setCancelTarget(b); }}
        />
      )}
      {cancelTarget && (
        <CancelDialog
          booking={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onConfirm={handleCancel}
          loading={cancelling}
        />
      )}
      <Snackbar open={snack.open} autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.sev} variant="filled"
          onClose={() => setSnack({ ...snack, open: false })}
          sx={{ borderRadius: 2, fontWeight: 600 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );

  // MODE 1 — DIALOG (called from NursingPhysiotherapyPage)
  if (isDialogMode) {
    return (
      <ThemeProvider theme={theme}>
        <Dialog
          open={open} onClose={onClose}
          maxWidth="md" fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : 3,
              overflow: "hidden",
              height: isMobile ? "100%" : "85vh",
              maxHeight: isMobile ? "100%" : "85vh",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          {/* Accent top bar */}
          <Box sx={{ height: 5, background: "linear-gradient(90deg, #006D77, #00949f)" }} />

          <DialogTitle sx={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            borderBottom: "1px solid #e8f4f5", pb: 1.5, pt: 2,
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <HistoryIcon sx={{ color: "#006D77", fontSize: 20 }} />
              <Typography fontWeight={800} fontSize={{ xs: 15, sm: 18 }} color="#006D77">
                My Bookings
              </Typography>
              {bookings.length > 0 && (
                <Chip label={bookings.length} size="small" color="primary" sx={{ fontWeight: 700, fontSize: 11 }} />
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton size="small" onClick={onRefresh} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ pt: 2.5, px: { xs: 1.5, sm: 2.5 }, overflowY: "auto", flex: 1 }}>
            <BookingsBody {...bodyProps} />
          </DialogContent>

          <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, borderTop: "1px solid #e8f4f5" }}>
            <Button onClick={onClose} color="inherit" variant="outlined">Close</Button>
          </DialogActions>
        </Dialog>

        {Modals}
      </ThemeProvider>
    );
  }

  // MODE 2 — STANDALONE PAGE (route: /my-NursingPhyservices)
  if (loading && !refreshing) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", bgcolor: "#F9FAFB" }}>
          <CircularProgress size={60} sx={{ color: "#006D77" }} />
          <Typography sx={{ mt: 2, fontSize: 16, color: "#6B7280" }}>Loading your bookings…</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <link href="https://fonts.googleapis.com/css2?family=Sora:wght@700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <Box sx={{ minHeight: "100vh", bgcolor: "#F9FAFB" }}>
        {/* Page header */}
        <Paper elevation={2} sx={{ bgcolor: "#fff", mb: 0 }}>
          <Container maxWidth="lg">
            <Box sx={{
              py: { xs: 1.5, sm: 2, md: 3 },
              display: "flex", alignItems: "center",
              justifyContent: "space-between", gap: 1, position: "relative",
            }}>
              <IconButton onClick={() => navigate(-1)} sx={{ flexShrink: 0, bgcolor: "white", boxShadow: 1, "&:hover": { bgcolor: "#f5f5f5" } }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h4" sx={{
                color: "#006D77", fontWeight: 600,
                fontSize: { xs: "1rem", sm: "1.4rem", md: "2rem" },
                textAlign: "center", flex: 1, fontFamily: "'Sora', sans-serif",
              }}>
                My Nursing & Physiotherapy Bookings
              </Typography>
              <IconButton onClick={onRefresh} disabled={refreshing} sx={{ position: "absolute", right: 0 }}>
                <RefreshIcon />
              </IconButton>
            </Box>
          </Container>
        </Paper>

        <Container maxWidth="lg" sx={{ py: 3, pb: 6 }}>
          <BookingsBody {...bodyProps} />
        </Container>
      </Box>

      {Modals}
    </ThemeProvider>
  );
}